import { useEffect, useRef, useState, type FormEvent } from 'react'

type Mode = 'totp' | 'backup'

interface TOTPFormProps {
  onSubmit: (code: { totp_code?: string; backup_code?: string }) => void
  isPending: boolean
  error: string | null
}

export function TOTPForm({ onSubmit, isPending, error }: TOTPFormProps) {
  const [mode, setMode] = useState<Mode>('totp')
  const [code, setCode] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [mode])

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (mode === 'totp') {
      onSubmit({ totp_code: code.trim() })
    } else {
      onSubmit({ backup_code: code.trim() })
    }
  }

  const toggleMode = () => {
    setMode((m) => (m === 'totp' ? 'backup' : 'totp'))
    setCode('')
  }

  const canSubmit = !isPending && (mode === 'totp' ? /^\d{6}$/.test(code) : code.trim().length >= 8)

  const label = mode === 'totp' ? 'TOTP-Code · 6 Ziffern' : 'Backup-Code · XXXX-XXXX'

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
      <p className="text-xs text-muted">Passwort akzeptiert — bitte den zweiten Faktor eingeben.</p>

      <div className="flex flex-col gap-2">
        <label htmlFor="code" className="tracked-caps text-[10px] text-muted">
          {label}
        </label>
        <input
          ref={inputRef}
          id="code"
          name="code"
          type="text"
          inputMode={mode === 'totp' ? 'numeric' : 'text'}
          autoComplete="one-time-code"
          required
          value={code}
          onChange={(e) => setCode(e.target.value)}
          maxLength={mode === 'totp' ? 6 : 9}
          disabled={isPending}
          className="w-full border border-hairline bg-void px-3 py-2.5 font-mono text-lg tracking-[0.25em] text-ink placeholder:text-faint transition-colors focus:border-bernstein focus:outline-none disabled:opacity-50"
          placeholder={mode === 'totp' ? '······' : 'XXXX-XXXX'}
          aria-invalid={error !== null}
        />
      </div>

      {error && (
        <p role="alert" className="font-mono text-xs text-loss">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={!canSubmit}
        className="tracked-caps mt-2 bg-bernstein px-6 py-3 text-[11px] text-void transition-colors hover:bg-bernstein-dim disabled:cursor-not-allowed disabled:bg-faint disabled:text-void/60"
      >
        {isPending ? 'Wird geprüft' : 'Bestätigen'}
      </button>

      <button
        type="button"
        onClick={toggleMode}
        disabled={isPending}
        className="self-start text-xs text-muted transition-colors hover:text-bernstein disabled:opacity-60"
      >
        {mode === 'totp' ? '→ Backup-Code verwenden' : '→ Zurück zu TOTP-Code'}
      </button>
    </form>
  )
}
